import { CartonData } from '@/types/CartonData.type';

type CartonWithOptionalSequence = Pick<CartonData, 'sequence'> & Partial<CartonData>;

/**
 * Next sequence for a new carton row (max existing sequence + 1, or 1 if empty).
 */
export function getNextCartonSequence(rows: CartonWithOptionalSequence[] | undefined): number {
  if (!rows?.length) {
    return 1;
  }
  let maxSequence = 0;
  for (const row of rows) {
    const sequence = row.sequence;
    if (typeof sequence === 'number' && Number.isFinite(sequence) && sequence > maxSequence) {
      maxSequence = sequence;
    }
  }
  return maxSequence + 1;
}

/**
 * Reassigns contiguous 1..n sequences from current array order.
 */
export function reindexCartonSequences<T extends CartonWithOptionalSequence>(rows: T[]): T[] {
  return rows.map((row, index) => ({
    ...row,
    sequence: index + 1,
  }));
}

/**
 * Stable sort by sequence ascending; rows without a valid sequence sort last.
 */
export function sortCartonsBySequence<T extends CartonWithOptionalSequence>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    const sequenceA =
      typeof a.sequence === 'number' && Number.isFinite(a.sequence)
        ? a.sequence
        : Number.POSITIVE_INFINITY;
    const sequenceB =
      typeof b.sequence === 'number' && Number.isFinite(b.sequence)
        ? b.sequence
        : Number.POSITIVE_INFINITY;
    if (sequenceA !== sequenceB) {
      return sequenceA - sequenceB;
    }
    return 0;
  });
}
